import type { ReactNode } from 'react'
import { StateEraserIcon } from '@/shared/ui/icons/StateEraserIcon'
import { StateSafeIcon } from '@/shared/ui/icons/StateSafeIcon'

type TextFieldSize = 'default' | 'large' | 'large-button'

/**
 * 아래 메시지의 성격(시안 666:28889 · 666:28893 «인풋 변화»).
 * `error` 는 테두리까지 빨개지고, `success` 는 테두리를 그대로 두고 동근 체크를 붙인다.
 */
export type TextFieldTone = 'default' | 'error' | 'success'

const MESSAGE_TONE: Record<TextFieldTone, string> = {
  default: 'text-black-100',
  error: 'text-[#f54550]',
  success: 'text-[#27c241]',
}

const BOX: Record<TextFieldSize, string> = {
  default: 'h-[46px] rounded-xl px-3',
  large: 'h-[52px] rounded-[14px] px-3.5',
  'large-button': 'h-[52px] rounded-[14px] pr-[7px] pl-3.5',
}

const TEXT: Record<TextFieldSize, string> = {
  default: 'text-body-md',
  large: 'text-body-lg',
  'large-button': 'text-body-lg',
}

/** 시안 Input 의 Label — Lage 판은 16/1.35 다(1288:20274 · 1288:18993 실측). Default 판은 아직 대조하지 않아 14 로 둔다. */
const LABEL: Record<TextFieldSize, string> = {
  default: 'text-body-sm',
  large: 'text-body-md',
  'large-button': 'text-body-md',
}

const LEADING_ICON: Record<TextFieldSize, string> = {
  default: '[&>svg]:h-5 [&>svg]:w-5 text-black-400',
  large: '[&>svg]:h-6 [&>svg]:w-6 text-black-300',
  'large-button': '[&>svg]:h-6 [&>svg]:w-6 text-black-300',
}

export function TextField({
  size = 'default',
  label,
  required = false,
  message,
  tone = 'default',
  count,
  leadingIcon,
  onClear,
  clearLabel = '입력 지우기',
  button,
  className = '',
  ...rest
}: {
  size?: TextFieldSize
  label?: string
  required?: boolean
  message?: string
  tone?: TextFieldTone
  count?: { current: number; max: number }
  leadingIcon?: ReactNode
  onClear?: () => void
  clearLabel?: string
  button?: { text: string; onClick: () => void }
  className?: string
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'className'>) {
  const disabled = rest.disabled === true

  return (
    <div
      className={[
        // 라벨과 칸 사이는 6 이다 — 시안 Input 의 바깥 묶음 gap(1288:20274 실측).
        'flex w-full flex-col items-start gap-1.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label !== undefined && (
        <label
          className={`flex items-start justify-center gap-1 font-normal text-black-600 ${LABEL[size]}`}
        >
          <span>{label}</span>
          {required && (
            <span
              aria-hidden="true"
              className="mt-0.5 h-1 w-1 shrink-0 rounded-full bg-[#f54550]"
            />
          )}
        </label>
      )}

      <div
        className={[
          'flex min-h-10 w-full items-center gap-2 overflow-clip border py-2.5',
          BOX[size],
          disabled
            ? 'border-white-400 bg-white-300'
            : tone === 'error'
              ? 'border-[#f54550] bg-white-50'
              : 'border-white-400 bg-white-50 focus-within:border-white-600',
        ].join(' ')}
      >
        {leadingIcon !== undefined && (
          <span
            aria-hidden="true"
            className={[
              'flex shrink-0 items-center justify-center',
              disabled ? 'text-white-600 [&>svg]:h-5 [&>svg]:w-5' : LEADING_ICON[size],
            ].join(' ')}
          >
            {leadingIcon}
          </span>
        )}

        <input
          className={[
            'min-w-px flex-1 shrink-0 grow basis-0 border-0 bg-transparent p-0 font-[inherit] outline-none',
            TEXT[size],
            disabled ? 'text-white-600 placeholder:text-white-600' : 'text-black-500 placeholder:text-black-100',
            // Lage_Button 판만 글자에 한 줄 말줄임이 걸린다.
            size === 'large-button' ? 'overflow-hidden text-ellipsis whitespace-nowrap' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        />

        {count !== undefined && (
          <span className="flex shrink-0 items-center gap-0.5 text-body-sm font-normal whitespace-nowrap">
            <span className="text-right text-[#501fa8]">{count.current}</span>
            <span className="text-black-100">/{count.max}</span>
          </span>
        )}

        {onClear !== undefined && (
          <button
            type="button"
            aria-label={clearLabel}
            disabled={disabled}
            onClick={onClear}
            className={[
              'flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 disabled:cursor-not-allowed',
              disabled ? 'text-white-600' : 'text-white-700',
              size === 'default' ? '[&>svg]:h-5 [&>svg]:w-5' : '[&>svg]:h-6 [&>svg]:w-6',
            ].join(' ')}
          >
            <StateEraserIcon />
          </button>
        )}

        {size === 'large-button' && button !== undefined && (
          <button
            type="button"
            disabled={disabled}
            onClick={button.onClick}
            className={[
              'flex h-10 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-[10px] border-0 p-2.5 text-body-sm font-medium whitespace-nowrap disabled:cursor-not-allowed',
              disabled ? 'bg-white-400 text-white-600' : 'bg-white-200 text-black-600',
            ].join(' ')}
          >
            {button.text}
          </button>
        )}
      </div>

      {message !== undefined && (
        <span
          className={`flex items-center gap-1 text-body-sm font-normal ${MESSAGE_TONE[tone]}`}
          {...(tone === 'error' ? { role: 'alert' as const } : {})}
        >
          {tone === 'success' && (
            <span className="flex shrink-0 items-center">
              <StateSafeIcon />
            </span>
          )}
          {message}
        </span>
      )}
    </div>
  )
}
